import { html, LitElement, nothing, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { guard } from 'lit/directives/guard.js';
import { map } from 'lit/directives/map.js';
import { range } from 'lit/directives/range.js';
import { repeat } from 'lit/directives/repeat.js';
import {
  type GameDetails,
  LabelValue,
  type Lobby,
  type LobbyGame,
  type PageDisplay,
  type Tag,
  TileType,
} from '../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { SubHelper } from '../../_ui-core_/base/package/patterns/package/store';
import { I18n } from '../../_ui-core_/base/package/services/package/i18n/i18n.de';
import { createDeferred } from '../../_ui-core_/base/package/util/async';
import { dispatchCustomEvent } from '../../_ui-core_/base/package/util/event';
import { updateRobotsTag } from '../../_ui-core_/base/package/util/html-helper';
import { paintTick } from '../../_ui-core_/base/package/util/render';
import '../../_ui-core_/components/filter/ui-filter';
import type { FilterItem, FilterSelectDetail } from '../../_ui-core_/components/filter/ui-filter';
import '../../_ui-core_/components/ui-game-tile/ui-game-tile';
import type { GameTileLabel } from '../../_ui-core_/components/ui-game-tile/ui-game-tile';
import '../../_ui-core_/components/ui-games-grid/ui-games-grid';
import '../../_ui-core_/components/ui-placeholder/ui-placeholder';
import App from '../../app';
import { HeaderOptionsEventName } from '../../app/package/base/router/router-types/sidebar-types';
import { type LoginObject, LoginStatus } from '../../app/package/base/service/login/login-domain';
import { TrackableEventAction } from '../../app/package/base/service/tracking/tracking-service';
import { Layout } from '../../app/package/base/types';
import { OFTEN_SEARCHED_CATEGORY_SLUG } from '../../constants';
import placeholderStyles from '../../styles/placeholder.css?inline';
import { getFeaturesFromGames, getThemesFromGames } from './filter-utils';
import style from './styles.css?inline';

const CName = 'category-page';

export const CategoryPageId = {
  STUDIOS: 'studios',
} as const;

export type CategoryPageId = (typeof CategoryPageId)[keyof typeof CategoryPageId];

/**
 * @fires header-options-update - Event fired when the header should be updated
 * @fires restore-scroll-position - Event fired when the scroll position should be restored
 */
@customElement(CName)
export class CategoryPage extends LitElement {
  static readonly styles = [unsafeCSS(placeholderStyles), unsafeCSS(style)];

  @property({ type: String }) categoryId: string;
  @property({ type: String }) personalizationCategorySlug: string;
  @property({ type: String }) tagSlug: string;

  @state() private _activeFilter = ''; // Empty string to show all
  @state() private _filterOptions?: FilterItem[];
  @state() private _games: LobbyGame[] | GameDetails[] = [];
  @state() private _layout: Layout | undefined;
  @state() private _loading = true;
  @state() private _userSession: LoginObject | undefined;
  @state() private _pageDisplay: PageDisplay = {
    tile: TileType.P1,
    shouldShowLabels: true,
  };

  private readonly _limit = 2_000; // max. games to load
  private readonly _lobbyIsReady = createDeferred<void>();
  private readonly _renderGamesAtOnce = 12; // 4 rows on an iPhone
  private readonly _minGamesToShowUIDesktop = 50;
  private readonly _minGamesToShowUIMobile = 12;
  private readonly _subHelper = new SubHelper();

  private _batchAbort: AbortController | null = null;
  private _categoryNameInternal: string;
  private _categoryPageTaskId: number | null = null;
  private _categoryPageTilesTaskId: number | null = null;
  private readonly _clickActionCache = new Map<string, (ev?: PointerEvent) => void>();
  private _gamesTaskId: number | null = null;
  private readonly _infoActionCache = new Map<string, (ev?: PointerEvent) => void>();
  private _lobby: Lobby;
  private _renderedCategoryId: string;
  private _tagNameInternal: string;
  private _userBased = false;

  @property({ type: String })
  get categoryName() {
    return this._categoryNameInternal;
  }

  set categoryName(value: string) {
    const oldValue = this._categoryNameInternal;
    this._categoryNameInternal = value;
    this.requestUpdate('categoryName', oldValue);
    this._headerUpdate(this.categoryName, this.categoryId);
  }

  @property({ type: String })
  get tagName() {
    return this._tagNameInternal;
  }

  set tagName(value: string) {
    const oldValue = this._tagNameInternal;
    this._tagNameInternal = value;
    this.requestUpdate('tagName', oldValue);
    this._headerUpdate(this.tagName, this.tagName);
  }

  connectedCallback() {
    super.connectedCallback();

    this._categoryPageTaskId = window.$app.renderService.addTask('category-page');
    this._categoryPageTilesTaskId = window.$app.prerenderService.addTask('category-page-tiles');
    this._subHelper.addSub(App.instance.layoutStore, (_) => this._updateLayout(_), true);

    App.instance.login.getFinalLoginStatus().then(() => {
      if (!this.isConnected) return;
      this._subHelper.addSub(App.instance.content.getLobby(), (_) => this._updateLobby(_ as Lobby), true); // We need the lobby to get the meta data of the selected category.
      this._subHelper.addSub(window.$app.loginStore, (_) => this._updateSession(_), true);
      App.instance.router.scrollToTop();

      App.instance.trackingStore.next({
        ...App.instance.trackingStore.value,
        winType: undefined,
        gameFilters: this._getFiltersForTracking(),
      });

      const title = this.categoryName || this.tagName;

      if (title) {
        const trackingSource = this.categoryId || this.tagName;
        this._headerUpdate(title, trackingSource);
      }

      if (this.categoryId === 'userRecommendation') {
        updateRobotsTag('none');
      }

      if (this.tagSlug) {
        updateRobotsTag('noindex');
      }
    });
    window.$app.logger.log(`category page connected (category: '${this.categoryId}')`);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._batchAbort?.abort();
    this._subHelper.unsubscribeAll();
    this._clickActionCache.clear();
    this._infoActionCache.clear();
  }

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (this.categoryId !== this._renderedCategoryId) {
      this._renderedCategoryId = this.categoryId;
      window.$app.logger.log(`update games after component update (category: '${this.categoryId}')`);
      this._gamesTaskId = window.$app.renderService.addTask('games');
      this._getGamesForCategory();
      return;
    }
    if (changedProperties.has('tagSlug') || changedProperties.has('tagName')) {
      this._gamesTaskId = window.$app.renderService.addTask('games');
      this._getGamesForTag();
    }
  }

  render() {
    return html`
      <div class="filter-page">
        <ui-a11y-title>${this._tagNameInternal || this._categoryNameInternal}</ui-a11y-title>
        ${this._renderFilter()}${this._renderContent()}
      </div>
    `;
  }

  private _renderFilter() {
    if (this._filterOptions === undefined || this._filterOptions.length === 0) {
      return nothing;
    }
    // Remove current tag from array if page is a tag list page
    const items = this.tagName
      ? this._filterOptions.filter((option) => option.name !== this.tagName)
      : this._filterOptions;
    return html`<ui-filter @select=${this._handleFilterSelection} .items=${items} .button=${true}></ui-filter>`;
  }

  private _renderGames() {
    // TODO: create custom properties for tile type size values (109px, 156px, 327px) and use them everywhere. start here: ui-game-tile-sizes.styles.ts
    let minTileWidth = '109px';
    if (this._pageDisplay.tile === TileType.P2) {
      minTileWidth = '156px';
    } else if (this._pageDisplay.tile === TileType.L1) {
      minTileWidth = '327px';
    }
    return html`
      <ui-games-grid class="games-grid" style=${`--_min-width:${minTileWidth}`}>
        ${guard([this._games, this._activeFilter], () => this._renderGameTiles(this._games as LobbyGame[]))}
      </ui-games-grid>
    `;
  }

  private _renderContent() {
    if (this._games.length > 0) {
      return this._renderGames();
    }
    return this._renderPlaceholderTiles();
  }

  private _renderPlaceholderTiles() {
    const tileType = this._pageDisplay.tile;
    const label = this._loading
      ? window.$app.$t.get(I18n.category.tiles.placeholder.loading)
      : window.$app.$t.get(I18n.category.tiles.placeholder.empty);
    return html`
      <div class="ph-tiles" data-label=${label}>
        ${map(range(4), () => html`<ui-placeholder .type=${tileType}></ui-placeholder>`)}
      </div>
    `;
  }

  private _renderGameTile(tile: LobbyGame, _position: number) {
    // Reuse stable closure references to avoid defeating Lit's === diffing
    if (!this._clickActionCache.has(tile.id)) {
      this._clickActionCache.set(tile.id, (ev?: PointerEvent) => {
        const idx = this._getGamePosition(tile.id);
        this._handleGameTileClick(ev as PointerEvent, tile, idx, Boolean(tile.vid));
      });
    }
    if (!this._infoActionCache.has(tile.id)) {
      this._infoActionCache.set(tile.id, (ev?: PointerEvent) => {
        const idx = this._getGamePosition(tile.id);
        this._trackGameClick(tile, idx, TrackableEventAction.GAME_INFO, Boolean(tile.vid));
        this._showGameInfo(ev as PointerEvent, tile.id, tile.slug);
      });
    }

    return html`
      <ui-game-tile
        .activeUser=${this._userSession?.loginStatus === LoginStatus.LOGGED_IN}
        .adaptiveWidth=${true}
        .clickAction=${this._clickActionCache.get(tile.id)!}
        .gameId=${tile.id}
        .slug=${tile.slug}
        .imageSrc=${tile.img ?? ''}
        .infoAction=${this._infoActionCache.get(tile.id)!}
        .isMaintenanceMode=${tile.maintenance === true}
        .labels=${this._getLabels(tile)}
        .title=${tile.title}
        .type=${this._pageDisplay.tile}
        .videoSrc=${tile.vid}
      ></ui-game-tile>
    `;
  }

  private _renderGameTiles(lobbyGames: LobbyGame[]) {
    let games = lobbyGames;
    // Optionally filter games
    if (this._activeFilter !== '') {
      games = games.filter(
        (game) => game.themes?.includes(this._activeFilter) || game.features?.includes(this._activeFilter),
      );
    }
    // Render games with key to avoid chaotic re-rendering.
    return repeat(
      games,
      (game: LobbyGame) => game.id,
      (game: LobbyGame, i) => this._renderGameTile(game, i),
    );
  }

  /**
   * Render groups of games to avoid slow render cycles.
   * Each group is rendered after a paint tick to yield to the browser.
   * Uses AbortController to cancel the previous batch chain when a new
   * category/tag loads or the component disconnects.
   */
  private async _renderGroupOfGames(games: LobbyGame[] | GameDetails[]) {
    // Cancel any in-flight batch chain
    this._batchAbort?.abort();
    const controller = new AbortController();
    this._batchAbort = controller;

    let rendered = 0;
    const minGamesToShowUI =
      this._layout === Layout.Mobile ? this._minGamesToShowUIMobile : this._minGamesToShowUIDesktop;

    while (rendered < games.length) {
      if (controller.signal.aborted || !this.isConnected) return;

      rendered = Math.min(rendered + this._renderGamesAtOnce, games.length);
      this._games = games.slice(0, rendered) as LobbyGame[] | GameDetails[];

      if (this._gamesTaskId && rendered >= minGamesToShowUI) {
        this._removeGamesRenderTask();
      }

      if (rendered < games.length) {
        await paintTick();
      }
    }

    if (controller.signal.aborted || !this.isConnected) return;

    window.$app.logger.log('update games. done.', rendered, games.length);
    dispatchCustomEvent(this, 'restore-scroll-position');
    this._trackGamesImpression();
    this._removeGamesRenderTask();
    this._removeTilesPrerenderTask();
  }

  private _removeGamesRenderTask() {
    if (this._gamesTaskId !== null) {
      this._gamesTaskId = window.$app.renderService.removeTask(this._gamesTaskId);
    }
  }

  private _handleFilterSelection(ev: CustomEvent) {
    const { name, slug } = ev.detail as FilterSelectDetail;
    // Set to empty if 'All' is selected. Otherwise use the name.
    this._activeFilter = slug === '' ? '' : name;
    this._filterOptions!.forEach((option) => {
      option.active = option.name === name;
    });
    // Reassign to trigger re-render
    this._filterOptions = [...this._filterOptions!];
  }

  private _getGamePosition(gameId: string): number {
    return (this._games as LobbyGame[]).findIndex((g) => g.id === gameId);
  }

  private _handleGameTileClick(ev: PointerEvent, game: LobbyGame, position: number, isVideo?: boolean) {
    App.instance.trackingStore.next({
      ...App.instance.trackingStore.value,
      gameFilters: this._getFiltersForTracking(),
      winType: undefined,
    });
    if (window.$app.loginStore.value.loginStatus === LoginStatus.LOGGED_IN) {
      this._trackGameClick(game, position, TrackableEventAction.GAME_SELECTED, isVideo);
      window.$app.vaix.updateVaixData({
        categorySlug: this.personalizationCategorySlug,
        gameSlug: game.slug,
        page: App.instance.router.currentLocation().url,
        position,
      });
      App.instance.router.navigateToGame(true, game.id);
    } else {
      this._trackGameClick(game, position, TrackableEventAction.GAME_INFO, isVideo);
      this._showGameInfo(ev, game.id, game.slug);
    }
  }

  private _showGameInfo(ev: PointerEvent, gameId: string, slug: string) {
    App.instance.trackingStore.next({
      ...App.instance.trackingStore.value,
      gameFilters: this._getFiltersForTracking(),
      winType: undefined,
    });
    window.$app.logger.log('show game info', gameId);
    window.$app.track.gameInfo(gameId);
    this._stopPropagation(ev);
    this._navigateToGameInfo(slug);
  }

  private _navigateToGameInfo(slug: string) {
    App.instance.router.navigateToGameInfo(slug);
  }

  private _stopPropagation(ev: PointerEvent) {
    ev.stopPropagation();
    ev.preventDefault();
  }

  private _getLabels(game: LobbyGame): Array<GameTileLabel> {
    if (!this._pageDisplay.shouldShowLabels) {
      return [];
    }

    const getLabelText = (label: LabelValue) => {
      if (label === LabelValue.EXCLUSIVE) {
        return window.$app.$t.get(I18n.gameInfo.labels.exclusive);
      }

      if (label === LabelValue.NEW) {
        return window.$app.$t.get(I18n.gameInfo.labels.new);
      }

      return window.$app.$t.get(I18n.gameInfo.labels.top10);
    };

    const translatedLabels: Array<GameTileLabel> =
      game.labels?.map((label) => ({
        id: label,
        value: getLabelText(label),
      })) ?? [];

    return translatedLabels;
  }

  private async _getGamesForTag() {
    window.$app.logger.log(`get games of tag '${this.tagSlug}'/'${this.tagName}'…`);
    const games = await App.instance.content.getGamesForTag(this.tagSlug);
    if (!this.isConnected) return;
    window.$app.logger.log(`get games of tag '${this.tagSlug}'/'${this.tagName}'…`, games.length);
    this._updateGames(games);
  }

  private async _getGamesForCategory(): Promise<void> {
    this._loading = true;

    const lobbyCategory = await App.instance.content.findLobbyCategory(this.categoryId);
    if (!this.isConnected) return;
    if (lobbyCategory) {
      this._pageDisplay = this._layout === Layout.Mobile ? lobbyCategory.pageMobile : lobbyCategory.page;
      this._userBased = lobbyCategory.isUserBased;
    }

    if (this.categoryId.startsWith('GameStudio')) {
      this._pageDisplay.tile = TileType.P1;
    }

    const filterIds = this.categoryId;
    window.$app.logger.log(`filterGames: type '${this._pageDisplay.tile}' (${this._layout})`);
    const gamesPromise =
      this.personalizationCategorySlug === OFTEN_SEARCHED_CATEGORY_SLUG
        ? App.instance.content.getOftenSearchedGames(
            this._pageDisplay.tile,
            this._limit,
            'default',
            undefined,
            true, // includeFilterInfo — required for themes/features filter tags
          )
        : App.instance.content.filterGames(
            { filterId: filterIds, isUserBased: this._userBased },
            this._pageDisplay.tile,
            this._limit,
            'default',
            undefined,
            true, // includeFilterInfo — required for themes/features on /games/category
          );
    gamesPromise
      .then(async (games) => {
        if (!this.isConnected) return;
        if (this._userBased) {
          const detailedGames = await Promise.all(games.map((game) => this._getGameInfo(game.id)));
          if (!this.isConnected) return;
          await this._updateGames(detailedGames);
          return;
        }

        await this._updateGames(games);
      })
      .catch((err) => {
        if (!this.isConnected) return;
        window.$app.logger.log('filterGames error:', err);
        this._loading = false;
      });
    this._games = [];
  }

  private async _getGameInfo(gameId: string, integration?: string) {
    return await App.instance.content.getGameInfo(gameId.includes(':') ? gameId : `${integration}:${gameId}`);
  }

  private async _updateGames(games: LobbyGame[] | GameDetails[]) {
    window.$app.logger.log(`update games (total: ${games.length})`);
    this._loading = false;
    // Invalidate closure caches when game list changes
    this._clickActionCache.clear();
    this._infoActionCache.clear();

    window.$app.logger.log(`total games: ${games.length}, limit: ${this._limit}`);
    if (!Array.isArray(games) || games.length === 0) {
      return;
    }

    await this._getFilterOptions(games);

    if (window.$app.appConfig.prerenderMode) {
      // Render all games at once
      this._games = [...games] as LobbyGame[] | GameDetails[];
      this._removeGamesRenderTask();
      this._removeTilesPrerenderTask();
      return;
    }

    // Render games in small groups to avoid slow render cycles
    this._renderGroupOfGames(games);
  }

  private async _getFilterOptions(games: LobbyGame[] | GameDetails[]) {
    await this._lobbyIsReady.promise; // The lobby must be ready to get the tags.

    // Get all themes from games
    const optionsThemes: Tag[] = getThemesFromGames(games, this._lobby.tags);
    const optionsFeatures: Tag[] = getFeaturesFromGames(games, this._lobby.tags);
    // Enrich options with id and active state
    this._filterOptions = [...optionsThemes, ...optionsFeatures]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(
        (option) =>
          ({
            active: false,
            name: option.name,
            slug: option.slug,
          }) as FilterItem,
      );
    // Add 'All' option
    if (this._filterOptions.length) {
      this._filterOptions.unshift({
        active: true,
        name: window.$app.$t.get(I18n.base.all),
        slug: '',
      });
    }
  }

  private _updateLayout(layout: Layout) {
    this._layout = layout;
  }

  private _updateLobby(lobby: Lobby) {
    this._lobby = lobby;
    this._lobbyIsReady.resolve();
    if (this._categoryPageTaskId !== null) {
      this._categoryPageTaskId = window.$app.renderService.removeTask(this._categoryPageTaskId);
    }
  }

  private _removeTilesPrerenderTask() {
    if (this._categoryPageTilesTaskId !== null) {
      this._categoryPageTilesTaskId = window.$app.prerenderService.removeTask(this._categoryPageTilesTaskId);
    }
  }

  private _updateSession(session: LoginObject) {
    if (session.loginStatus === LoginStatus.LOGGED_OUT) {
      this._userSession = undefined;
    } else {
      this._userSession = { ...session };
    }
  }

  private _trackGameClick(game: LobbyGame, position: number, eventAction: TrackableEventAction, isVideo?: boolean) {
    const gameSource = this._getGameSourceTrackingString();

    App.instance.content
      .getGameInfo(game.id)
      .then((gameInfo) => {
        const params = {
          gameName: game.title,
          gameFilters: this._getFiltersForTracking(),
          gameCategory: game.labels?.join(' - '),
          gamePosition: position + 1,
          gameProvider: gameInfo.getStudio(),
          gameSource,
          select: isVideo ? 'video' : 'image',
        };

        if (gameInfo) {
          App.instance.trackingStore.next({
            ...App.instance.trackingStore.value,
            ...params,
            gameSource,
            winType: undefined,
          });
          window.$app.track.gamePlay({
            eventAction,
            ...params,
            gameSource,
          });
        }
      })
      .catch((err) => {
        window.$app.logger.warn(`Error fetching game info '${game.id}'`, err);
      });
  }

  private _getFiltersForTracking() {
    return this._activeFilter.length ? this._activeFilter : 'Alle';
  }

  private _getGameSourceTrackingString(): string {
    const segments = [
      App.instance.trackingStore.value.gameSource?.trim(),
      this.tagName?.replace(/\s+/g, '').trim(),
      this.categoryId?.replace(/\s+/g, '').trim(),
    ].filter((segment) => segment);

    const uniqueSegments: string[] = [];
    for (const segment of segments) {
      if (segment && !uniqueSegments.some((s) => s.toLowerCase() === segment.toLowerCase())) {
        uniqueSegments.push(segment);
      }
    }

    return uniqueSegments.join(' - ');
  }

  private _trackGamesImpression() {
    window.$app.vaix.trackGamesImpressionVaix([
      {
        location: this.categoryId ?? this.tagSlug,
        gameSlugs: this._games ? this._games.map((game) => game.slug) : [''],
      },
    ]);
  }

  private _headerUpdate(value: string, trackingSouce: string) {
    dispatchCustomEvent(this, HeaderOptionsEventName, {
      title: value,
      trackingSource: trackingSouce,
      isGoBackShown: true,
    });
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: CategoryPage;
  }
}
